import { z } from "zod";

/**
 * Persisted media / document JSON shape used on Delivery fields such as
 * `docs` and `validationDocs`, and mirrored by mobile `UploadedFile`.
 *
 * - `key` — raw S3 object key (source of truth; unencoded)
 * - `url` — public object URL derived via `@repo/utils` `buildS3ObjectUrl`
 * - `name` / `label` — human-facing original filename for UI
 *
 * Do not put device-local paths (`localUri`) here — that is mobile ephemeral
 * state and must not be part of the cross-app persisted contract.
 *
 * Always derive `url` from `key` with the shared S3 helpers; do not hand-build
 * `s3.amazonaws.com` URLs from raw keys (encoding / region bugs).
 */
export const StoredMediaFileSchema = z.object({
  key: z.string(),
  url: z.string().nullable(),
  name: z.string().optional().nullable(),
  label: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  origin: z.enum(["SYSTEM", "USER"]).optional().nullable(),
  documentType: z.string().optional().nullable(),
  id: z.union([z.string(), z.number()]).optional().nullable(),
  docId: z.union([z.string(), z.number()]).optional().nullable(),
  tempObjectUrl: z.string().optional().nullable(),
  version_status: z.string().optional().nullable(),
  document_state: z.string().optional().nullable(),
  rejectionReason: z.string().optional().nullable(),
  additionalInfo: z.string().optional().nullable(),
  handledBy: z.string().optional().nullable(),
  updatedAt: z.string().optional().nullable(),
  removedAt: z.string().optional().nullable(),
  renewalOpen: z.boolean().optional().nullable(),
  expiresAt: z.string().optional().nullable(),
  approvedExpiresAt: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional().nullable(),
});

export const StoredMediaFilesSchema = z.array(StoredMediaFileSchema);

export type StoredMediaFile = z.infer<typeof StoredMediaFileSchema>;
export type StoredMediaFiles = z.infer<typeof StoredMediaFilesSchema>;
